import {
  applicantSection,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  legalInterestAttachment,
  legalInterestSection,
  resourceFeeInfo,
} from "modules/ebiForms/definitions/sharedGeodesyEnvironment";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G2. Wniosek o udostępnienie materiałów powiatowego zasobu geodezyjnego i kartograficznego — art. 40a–40e i 40g ustawy —
 * Prawo geodezyjne i kartograficzne; wzór ogólnopolski: „Formularz P” z uszczegółowieniami P1–P6 — załącznik nr 3 do
 * rozporządzenia MFiG z 28.07.2026 r. (Dz.U. 2026 poz. 1068), https://eli.gov.pl/api/acts/DU/2026/1068/text/O/D20261068.pdf
 * (pobrany 22.10.2026, s. 12–19). Odwzorowano: 7. materiały, 8. cel (8a odpłatnie ze współczynnikiem CL, 8b nieodpłatnie —
 * jeden cel), 10. sposób udostępnienia, 11. uwagi oraz szczegóły P1–P6 jako sekcje widoczne po zaznaczeniu materiału.
 * Obszar objęty wnioskiem (poz. 2–3 każdego z P1–P6) jest jedną wspólną sekcją; poz. 2 P2/P5 (dane podmiotów) — jedną sekcją
 * uzasadnienia. Opcję „usługa sieciowa udostępniania” (dotyczy wniosków składanych tą usługą) pominięto, a „inny sposób
 * odbioru” to EBI. Opłatę, licencję i Dokument Obliczenia Opłaty obsługuje urząd po złożeniu.
 */
const has = (value: string): EbiCondition => ({ field: "materialy", includes: value });
const MAP = has("mapa");
const EGIB = has("egib");
const GESUT = has("gesut");
const BDOT = has("bdot500");
const REPORTS = has("raporty");
const OTHER = has("inne");

const MAP_PRINTED: EbiCondition = { all: [MAP, { field: "p1Postac", equals: "drukowana" }] };
const OTHER_PRINTED: EbiCondition = { all: [OTHER, { field: "p6Postac", equals: "drukowana" }] };

/** Pozycje P2 i P5 z danymi osobowymi (przypisy „1” w P2 i „2” w P5) wymagają uzasadnienia dostępu. */
const PERSONAL_DATA: EbiCondition = {
  any: [
    { all: [EGIB, { any: ["pelny", "przedmiotowe", "opisowe"].map((v): EbiCondition => ({ field: "p2Zbior", includes: v })) }] },
    {
      all: [
        EGIB,
        {
          any: ["dzialkiPelna", "dzialkiOpisowe", "budynkiPelna", "budynkiOpisowe", "podmioty", "lokale"].map(
            (v): EbiCondition => ({ field: "p2Obiekty", includes: v }),
          ),
        },
      ],
    },
    {
      all: [
        REPORTS,
        {
          any: [
            { field: "p5Kartoteka", filled: true },
            { field: "p5Wykaz", includes: "podmioty" },
            { field: "p5Rejestr", includes: "gruntyPelne" },
            { field: "p5Rejestr", includes: "budynki" },
            { field: "p5Rejestr", includes: "lokale" },
          ],
        },
      ],
    },
  ],
};

const FREE: EbiCondition = { field: "cel", in: ["edukacja", "badania", "sluzby", "obronnosc", "gesutPierwsze"] };
const AREA_IN_ATTACHMENT: EbiCondition = { field: "obszarSposob", equals: "zalacznik" };
const COORDINATES: EbiCondition = { field: "obszarSposob", in: ["poligon", "zalacznik"] };
const SHIPPING: EbiCondition = { field: "sposobUdostepnienia", equals: "wysylka" };

const COORDINATE_SYSTEM = [
  { value: "pl2000", label: "PL-2000" },
  { value: "inny", label: "inny (dopuszczony przez organ prowadzący zasób)" },
];

const G2: IEbiFormDefinition = {
  code: "G2",
  version: 1,
  title: "Wniosek o udostępnienie materiałów powiatowego zasobu geodezyjnego i kartograficznego",
  category: EEbiFormCategory.Geodesy,
  office: EEbiFormOffice.County,
  description: "Mapa zasadnicza lub ewidencyjna, dane EGiB, GESUT, BDOT500, raporty i inne materiały zasobu — formularz P z uszczegółowieniami P1–P6.",
  keywords: ["mapa zasadnicza", "mapa ewidencyjna", "zasób geodezyjny", "pzgik", "gesut", "bdot500", "egib", "formularz P", "geodezja"],
  organ: "Starosta — ośrodek dokumentacji geodezyjnej i kartograficznej",
  legalBasis:
    "art. 40a–40e i art. 40g ustawy z dnia 17 maja 1989 r. — Prawo geodezyjne i kartograficzne; rozporządzenie Ministra Finansów i Gospodarki z dnia 28 lipca 2026 r. (Dz.U. 2026 poz. 1068) — wzór: załącznik nr 3 „Formularz P”",
  request: "Wnoszę o udostępnienie materiałów powiatowego zasobu geodezyjnego i kartograficznego wskazanych w części 7, w celu określonym w części 8.",
  feeLabel: "wg stawek",
  fee: "Opłata za udostępnienie według tabel załącznika do Prawa geodezyjnego i kartograficznego (z uwzględnieniem współczynnika CL wynikającego z celu), waloryzowana co roku — ustalana w Dokumencie Obliczenia Opłaty i wnoszona przed udostępnieniem. Bez opłaty skarbowej; pełnomocnictwo — 17 zł.",
  sections: [
    applicantSection("1. Wnioskodawca"),
    representationSection(),
    {
      code: "materialyWniosku",
      title: "7. Określenie materiałów będących przedmiotem wniosku",
      description: "Informacje o aktualnie dostępnych materiałach powiatowego zasobu udostępnia organ prowadzący ten zasób.",
      fields: [
        {
          code: "materialy",
          type: "checkboxes",
          label: "Materiały",
          required: true,
          options: [
            { value: "mapa", label: "Mapa zasadnicza lub mapa ewidencji gruntów i budynków (szczegóły — P1)" },
            { value: "egib", label: "Baza danych ewidencji gruntów i budynków (EGiB) (szczegóły — P2)" },
            { value: "gesut", label: "Baza danych geodezyjnej ewidencji sieci uzbrojenia terenu (GESUT) (szczegóły — P3)" },
            {
              value: "bdot500",
              label:
                "Baza danych obiektów topograficznych o szczegółowości zapewniającej tworzenie standardowych opracowań kartograficznych w skalach 1:500 – 1:5000 (BDOT500) (szczegóły — P4)",
            },
            { value: "raporty", label: "Raporty tworzone na podstawie bazy danych EGiB (szczegóły — P5)" },
            { value: "inne", label: "Inne materiały (szczegóły — P6)" },
          ],
        },
        {
          code: "oznaczenieWnioskodawcy",
          type: "text",
          label: "Oznaczenie wniosku nadane przez wnioskodawcę (nieobowiązkowo)",
          width: "half",
          maxLength: 100,
        },
      ],
    },
    {
      code: "celPobrania",
      title: "8. Cel pobrania materiałów",
      description: "Dopuszczalne jest wskazanie tylko jednego celu. Cel decyduje o współczynniku CL, a więc o wysokości opłaty.",
      fields: [
        {
          code: "cel",
          type: "radio",
          label: "Cel",
          required: true,
          options: [
            {
              value: "wlasne",
              label: "8a. odpłatnie — dla potrzeb własnych niezwiązanych z działalnością gospodarczą, bez prawa publikacji w sieci Internet (CL 1.0)",
            },
            { value: "dowolne", label: "8a. odpłatnie — dla dowolnych potrzeb (CL 2.0)" },
            {
              value: "gesutKolejne",
              label:
                "8a. odpłatnie — w celu kolejnego udostępnienia zbiorów danych dotyczących sieci uzbrojenia terenu podmiotowi władającemu siecią uzbrojenia terenu (CL 0.1)",
            },
            {
              value: "edukacja",
              label:
                "8b. nieodpłatnie w postaci elektronicznej — na cele edukacyjne jednostkom organizacyjnym wchodzącym w skład systemu oświaty, uczelniom, podmiotom pożytku publicznego",
            },
            { value: "badania", label: "8b. nieodpłatnie w postaci elektronicznej — w celu prowadzenia badań naukowych / prac rozwojowych" },
            {
              value: "sluzby",
              label:
                "8b. nieodpłatnie w postaci elektronicznej — w celu realizacji ustawowych zadań w zakresie ochrony bezpieczeństwa wewnętrznego państwa i jego porządku konstytucyjnego — służbom specjalnym",
            },
            {
              value: "obronnosc",
              label: "8b. nieodpłatnie w postaci elektronicznej — w celu realizacji zadań w zakresie obronności państwa — Ministrowi Obrony Narodowej",
            },
            {
              value: "gesutPierwsze",
              label:
                "8b. nieodpłatnie w postaci elektronicznej — w celu pierwszego udostępnienia zbiorów danych dotyczących sieci uzbrojenia terenu podmiotowi władającemu siecią uzbrojenia terenu",
            },
          ],
        },
      ],
    },
    {
      code: "p1",
      title: "P1. Szczegóły — mapa zasadnicza lub mapa ewidencji gruntów i budynków",
      visibleWhen: MAP,
      fields: [
        {
          code: "p1Mapa",
          type: "checkboxes",
          label: "Mapa",
          required: true,
          options: [
            { value: "zasadnicza", label: "zasadnicza" },
            { value: "ewidencyjna", label: "ewidencji gruntów i budynków" },
          ],
        },
        {
          code: "p1Postac",
          type: "radio",
          label: "Postać",
          required: true,
          width: "half",
          options: [
            { value: "wektorowa", label: "wektorowa" },
            { value: "rastrowa", label: "rastrowa" },
            { value: "drukowana", label: "drukowana" },
          ],
        },
        {
          code: "p1Skala",
          type: "radio",
          label: "Skala",
          required: true,
          width: "half",
          hint: "Podstawowa skala mapy zasadniczej to 1:500. Część obiektów może nie być widoczna na mapach w innych skalach.",
          options: [
            { value: "500", label: "1:500" },
            { value: "1000", label: "1:1000" },
            { value: "2000", label: "1:2000" },
            { value: "5000", label: "1:5000" },
          ],
        },
        {
          code: "p1Format",
          type: "select",
          label: "Format wydruku",
          required: true,
          visibleWhen: MAP_PRINTED,
          width: "third",
          options: ["A4", "A3", "A2", "A1", "A0"].map((format) => ({ value: format, label: format })),
        },
        {
          code: "p1Kolorystyka",
          type: "select",
          label: "Kolorystyka wydruku",
          required: true,
          visibleWhen: MAP_PRINTED,
          width: "third",
          options: [
            { value: "czarnoBiala", label: "czarno-biała" },
            { value: "kolorowa", label: "kolorowa" },
          ],
        },
        { code: "p1Egzemplarze", type: "number", label: "Liczba egzemplarzy", required: true, visibleWhen: MAP_PRINTED, width: "third", min: 1, max: 50, defaultValue: "1" },
      ],
    },
    {
      code: "p2",
      title: "P2. Szczegóły — zbiór danych bazy danych EGiB (w postaci elektronicznej)",
      description: "Zaznacz zbiór danych albo wybrane obiekty. Pozycje oznaczone (*) zawierają lub mogą zawierać dane osobowe — wypełnij część „Uzasadnienie”.",
      visibleWhen: EGIB,
      fields: [
        {
          code: "p2Zbior",
          type: "checkboxes",
          label: "Zbiór danych EGiB [ha]",
          requiredWhen: { not: { field: "p2Obiekty", filled: true } },
          options: [
            { value: "pelny", label: "pełny zbiór danych (przedmiotowe i podmiotowe) (*)" },
            { value: "przedmiotowe", label: "dane przedmiotowe (geometryczne i opisowe) (*)" },
            { value: "geometryczne", label: "dane przedmiotowe (tylko geometryczne)" },
            { value: "opisowe", label: "dane przedmiotowe (tylko opisowe) (*)" },
          ],
        },
        {
          code: "p2Obiekty",
          type: "checkboxes",
          label: "Wybrane obiekty zbioru danych EGiB [liczba obiektów]",
          requiredWhen: { not: { field: "p2Zbior", filled: true } },
          options: [
            { value: "dzialkiPelna", label: "działki ewidencyjne — pełna informacja (*)" },
            { value: "dzialkiOpisowe", label: "działki ewidencyjne — tylko dane opisowe (*)" },
            { value: "dzialkiGeometryczne", label: "działki ewidencyjne — tylko dane geometryczne" },
            { value: "budynkiPelna", label: "budynki — pełna informacja (*)" },
            { value: "budynkiOpisowe", label: "budynki — tylko dane opisowe (*)" },
            { value: "budynkiGeometryczne", label: "budynki — tylko dane geometryczne" },
            { value: "podmioty", label: "podmioty wykazane w EGiB (*)" },
            { value: "lokale", label: "lokale (*)" },
            { value: "punkty", label: "punkty graniczne" },
            { value: "uzytki", label: "kontury użytków gruntowych" },
            { value: "klasyfikacyjne", label: "kontury klasyfikacyjne" },
            { value: "inneObiekty", label: "inne obiekty EGiB" },
          ],
        },
      ],
    },
    {
      code: "p3",
      title: "P3. Szczegóły — zbiór danych bazy danych GESUT (w postaci elektronicznej)",
      visibleWhen: GESUT,
      fields: [
        {
          code: "p3Zakres",
          type: "radio",
          label: "Zakres",
          required: true,
          defaultValue: "pelny",
          options: [
            { value: "pelny", label: "pełny zbiór danych" },
            { value: "wybrany", label: "wybrany zbiór danych GESUT (klasa obiektów)" },
          ],
        },
        {
          code: "p3Klasy",
          type: "checkboxes",
          label: "Klasy obiektów",
          required: true,
          visibleWhen: { field: "p3Zakres", equals: "wybrany" },
          options: [
            { value: "wodociagowa", label: "sieć wodociągowa" },
            { value: "kanalizacyjna", label: "sieć kanalizacyjna" },
            { value: "elektroenergetyczna", label: "sieć elektroenergetyczna" },
            { value: "gazowa", label: "sieć gazowa" },
            { value: "cieplownicza", label: "sieć ciepłownicza" },
            { value: "telekomunikacyjna", label: "sieć telekomunikacyjna" },
            { value: "specjalna", label: "sieć specjalna" },
            { value: "niezidentyfikowana", label: "sieć niezidentyfikowana" },
            { value: "towarzyszaceLiniowe", label: "urządzenia towarzyszące liniowe" },
            { value: "towarzyszaceInne", label: "inne urządzenia towarzyszące" },
          ],
        },
      ],
    },
    {
      code: "p4",
      title: "P4. Szczegóły — zbiór danych bazy danych BDOT500 (w postaci elektronicznej)",
      visibleWhen: BDOT,
      fields: [
        {
          code: "p4Zakres",
          type: "radio",
          label: "Zakres",
          required: true,
          defaultValue: "pelny",
          options: [
            { value: "pelny", label: "pełny zbiór danych" },
            { value: "wybrany", label: "wybrany zbiór danych BDOT500 (kategoria)" },
          ],
        },
        {
          code: "p4Kategorie",
          type: "checkboxes",
          label: "Kategorie",
          required: true,
          visibleWhen: { field: "p4Zakres", equals: "wybrany" },
          options: [
            { value: "budynki", label: "budynki niewykazane w ewidencji gruntów i budynków oraz obiekty budowlane trwale związane z budynkiem" },
            { value: "budowle", label: "budowle" },
            { value: "ogrodzenia", label: "ogrodzenia" },
            { value: "komunikacja", label: "komunikacja" },
            { value: "zagospodarowanie", label: "zagospodarowanie terenu" },
            { value: "sport", label: "sport i rekreacja" },
            { value: "wody", label: "wody" },
            { value: "rzezba", label: "rzeźba terenu" },
          ],
        },
      ],
    },
    {
      code: "p5",
      title: "P5. Szczegóły — raporty tworzone na podstawie bazy danych EGiB",
      description: "Zaznacz co najmniej jeden raport. Pozycje oznaczone (*) zawierają lub mogą zawierać dane osobowe — wypełnij część „Uzasadnienie”.",
      visibleWhen: REPORTS,
      fields: [
        {
          code: "p5Kartoteka",
          type: "checkboxes",
          label: "Kopia kartoteki",
          requiredWhen: { not: { any: [{ field: "p5Wykaz", filled: true }, { field: "p5Rejestr", filled: true }] } },
          options: [
            { value: "budynki", label: "budynków (*)" },
            { value: "lokale", label: "lokali (*)" },
          ],
        },
        {
          code: "p5Wykaz",
          type: "checkboxes",
          label: "Wykaz (skorowidz)",
          requiredWhen: { not: { any: [{ field: "p5Kartoteka", filled: true }, { field: "p5Rejestr", filled: true }] } },
          options: [
            { value: "dzialki", label: "działek ewidencyjnych" },
            { value: "podmioty", label: "podmiotów EGiB (*)" },
          ],
        },
        {
          code: "p5Rejestr",
          type: "checkboxes",
          label: "Kopia rejestru",
          requiredWhen: { not: { any: [{ field: "p5Kartoteka", filled: true }, { field: "p5Wykaz", filled: true }] } },
          options: [
            { value: "gruntyPelne", label: "gruntów — w pełnym zakresie (*)" },
            { value: "gruntyBezDanych", label: "gruntów — bez danych osobowych" },
            { value: "budynki", label: "budynków (*)" },
            { value: "lokale", label: "lokali (*)" },
          ],
        },
        {
          code: "p5Postac",
          type: "radio",
          label: "Postać",
          required: true,
          options: [
            { value: "elektroniczna", label: "elektroniczna" },
            { value: "drukowana", label: "drukowana" },
          ],
        },
      ],
    },
    {
      code: "p6",
      title: "P6. Szczegóły — inne materiały",
      visibleWhen: OTHER,
      fields: [
        {
          code: "p6Rodzaj",
          type: "radio",
          label: "Rodzaj materiału",
          required: true,
          options: [
            { value: "operat", label: "dokument wchodzący w skład operatu technicznego" },
            { value: "inne", label: "inne niewymienione" },
          ],
        },
        { code: "p6Opis", type: "textarea", label: "Określenie materiału (np. identyfikator operatu, nazwa dokumentu)", required: true, maxLength: 1000 },
        {
          code: "p6Postac",
          type: "radio",
          label: "Postać",
          required: true,
          width: "half",
          options: [
            { value: "elektroniczna", label: "elektroniczna" },
            { value: "drukowana", label: "drukowana" },
          ],
        },
        { code: "p6Egzemplarze", type: "number", label: "Liczba egzemplarzy", required: true, visibleWhen: OTHER_PRINTED, width: "half", min: 1, max: 50, defaultValue: "1" },
      ],
    },
    {
      code: "obszar",
      title: "Obszar objęty wnioskiem",
      description:
        "Dotyczy wszystkich zaznaczonych materiałów. Jeśli obszary dla poszczególnych materiałów są różne, opisz je w polu poniżej albo w uwagach.",
      fields: [
        {
          code: "obszarSposob",
          type: "radio",
          label: "Obszar określam przez wskazanie",
          required: true,
          options: [
            { value: "jednostki", label: "jednostek podziału terytorialnego kraju lub podziału dla celów EGiB (jednostki ewidencyjne, obręby, działki ewidencyjne)" },
            { value: "godlo", label: "godła arkusza mapy (dotyczy mapy — P1)" },
            { value: "poligon", label: "współrzędnych wielokąta (poligonu)" },
            { value: "zalacznik", label: "obszaru określonego w załączniku graficznym lub wektorowym" },
          ],
        },
        {
          code: "obszarOpis",
          type: "textarea",
          label: "Dane identyfikujące obszar",
          requiredWhen: { not: AREA_IN_ATTACHMENT },
          maxLength: 3000,
          hint: "Np. jednostka ewidencyjna, obręb i numery działek; godła arkuszy mapy; współrzędne wierzchołków poligonu.",
        },
        {
          code: "obszarUklad",
          type: "radio",
          label: "Układ współrzędnych",
          required: true,
          visibleWhen: COORDINATES,
          defaultValue: "pl2000",
          options: COORDINATE_SYSTEM,
        },
        {
          code: "obszarUkladInny",
          type: "text",
          label: "Inny układ współrzędnych",
          required: true,
          visibleWhen: { all: [COORDINATES, { field: "obszarUklad", equals: "inny" }] },
          maxLength: 100,
        },
      ],
    },
    legalInterestSection(
      PERSONAL_DATA,
      "Uzasadnienie wniosku o udostępnienie danych podmiotów (art. 20 ust. 2 pkt 1 PGiK) lub numeru księgi wieczystej — P2 / P5 poz. 2",
      "Wymagane przy pozycjach oznaczonych (*) w P2 i P5 — art. 24 ust. 5 ustawy — Prawo geodezyjne i kartograficzne.",
    ),
    {
      code: "sposobUdostepnieniaSekcja",
      title: "10. Sposób udostępnienia materiałów",
      fields: [
        {
          code: "sposobUdostepnienia",
          type: "radio",
          label: "Sposób udostępnienia",
          required: true,
          options: [
            { value: "ebi", label: "w Elektronicznym Biurze Interesanta (inny sposób odbioru)" },
            { value: "email", label: "wysyłka na wskazany adres e-mail" },
            { value: "uslugaOrganu", label: "udostępnienie inną usługą sieciową organu (np. FTP, SFTP, chmura)" },
            { value: "osobiscie", label: "odbiór osobisty" },
            { value: "wysylka", label: "wysyłka pod wskazany adres" },
          ],
        },
        { code: "emailUdostepnienia", type: "email", label: "Adres e-mail do wysyłki", required: true, visibleWhen: { field: "sposobUdostepnienia", equals: "email" } },
        {
          code: "adresWysylki",
          type: "radio",
          label: "Adres wysyłki",
          required: true,
          visibleWhen: SHIPPING,
          defaultValue: "naglowek",
          options: [
            { value: "naglowek", label: "jak w nagłówku (adres do korespondencji wnioskodawcy)" },
            { value: "inny", label: "inny" },
          ],
        },
        {
          code: "adresWysylkiInny",
          type: "text",
          label: "Inny adres wysyłki",
          required: true,
          visibleWhen: { all: [SHIPPING, { field: "adresWysylki", equals: "inny" }] },
          maxLength: 300,
        },
        {
          code: "nosnikWnioskodawcy",
          type: "checkbox",
          label: "Udostępnienie materiałów na nośniku danych elektronicznych dostarczonym przez wnioskodawcę",
          visibleWhen: { field: "sposobUdostepnienia", in: ["osobiscie", "wysylka"] },
          hint: "Do 4 GB koszt nośnika jest uwzględniony w opłacie; od 4 do 20 GB pobiera się 5 zł za nośnik, chyba że dostarczysz własny; powyżej 20 GB dane udostępnia się po dostarczeniu nośnika przez wnioskodawcę.",
        },
      ],
    },
    {
      code: "uwagi",
      title: "11. Dodatkowe wyjaśnienia i opłata",
      fields: [
        { code: "uwagiWnioskodawcy", type: "textarea", label: "Dodatkowe wyjaśnienia i uwagi wnioskodawcy (nieobowiązkowo)", maxLength: 3000 },
        resourceFeeInfo("infoOplata", "tabel"),
      ],
    },
    replySection("Sposób odbioru korespondencji w sprawie (w tym Dokumentu Obliczenia Opłaty i licencji)"),
  ],
  attachments: [
    {
      code: "zalacznikObszaru",
      label: "Załącznik graficzny lub wektorowy z zaznaczonym obszarem",
      hint: "PDF, obraz albo plik wektorowy (np. SHP, DXF, GML) w układzie wskazanym we wniosku.",
      visibleWhen: AREA_IN_ATTACHMENT,
      required: true,
    },
    {
      code: "dokumentUprawnienia",
      label: "Dokument potwierdzający uprawnienie do nieodpłatnego udostępnienia (art. 40a ust. 2 PGiK)",
      hint: "Np. zlecenie organu, dokument potwierdzający status jednostki.",
      visibleWhen: FREE,
      required: true,
    },
    legalInterestAttachment(PERSONAL_DATA, false),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G2;
